/* @noflow FIXME */
import invariant from 'invariant';
import * as React from 'react';

import Avatar from 'entities/Avatar';
import BoxBody from 'components/BoxBody.jsx';
import Button from 'components/Button.jsx';
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import Divider from 'components/Divider.jsx';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import Link from 'components/Link.jsx';
import List from 'components/List.jsx';
import MediaObject from 'components/MediaObject.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import ToastActions from 'actions/ToastActions';
import { mountModal } from 'lib/modal.js';
import { withRouter } from 'app/context/Router.jsx';

import GroupAvatarSelector from './GroupAvatarSelector.jsx';
import GroupForm from './GroupForm.jsx';
import GroupOptionsForm from './GroupOptionsForm.jsx';
import ReuseClassConfirmDialog from './ReuseClassConfirmDialog.jsx';
import RemoveClassConfirmationDialog from './RemoveClassConfirmDialog.jsx';

import './styles/GroupEditPanel.scss';

type Props = {
  group: Group,
  onCodeChange: Function,
  userIsTeacher: boolean,
  onRequestHide: Function,
  router: *,
};

type State = {
  name: ?string,
  code: ?string,
  canSubmit: boolean,
  group: Group,
  initialCode?: string,
  organizationId?: ?string,
};

class GroupEditPanel extends React.Component<Props, State> {
  state: State = {
    name: null,
    code: null,
    canSubmit: false,
    group: this.props.group,
  };

  componentWillMount() {
    const { group } = this.props;
    this.setState({ initialCode: group.getCode() });
  }

  handleAvatarSelected = (avatar: Avatar) => {
    GroupActions.patch(
      this.props.group,
      {
        group_avatar_id: avatar.id,
      },
      'Icon saved'
    );
  };

  handleInvalidChange = () => {
    this.setState({ name: null, code: null, canSubmit: false });
  };

  /* eslint-disable flowtype/space-before-type-colon */
  handleValidChange = (name: string, code: string, organizationId?: ?string) => {
    this.setState({ name, code, organizationId, canSubmit: true });
  };

  handleOptionsChange = (name: string, value: boolean) => {
    const underscored = name.replace(/([A-Z])/g, a => `_${a.toLowerCase()}`);
    GroupActions.patch(this.props.group, { [underscored]: value }).then(
      group => this.setState(group),
      ({ message }) => {
        this.setState({ group: this.props.group });
        ToastActions.showErrorToast(message);
      }
    );
  };

  handleSubmit = () => {
    const { code, name, organizationId } = this.state;
    invariant(code, 'missing group code in handleSubmit');

    const attributes = {
      name: code,
      class_name: name,
      group_avatar_id: this.refs.avatarSelector.getSelectedAvatarId(),
      organization_id: organizationId,
    };

    const update = () => {
      GroupActions.patch(this.props.group, attributes).then(
        () => {
          this.props.onRequestHide();
          if (this.state.initialCode !== code) {
            this.props.onCodeChange(code);
          }
        },
        error => ToastActions.showAPIError(error)
      );
    };

    if (attributes.name === this.props.group.getCode()) {
      update();
    } else {
      mountModal(
        this.renderConfirmDialog(
          `Are you sure you want to change your class code to @${attributes.name}`,
          'Current participants won’t be affected, but new participants ' +
            'must use this code to join your class.',
          'No',
          'Yes',
          update
        ),
        this
      );
    }
  };
  renderConfirmDialog(
    title: string,
    note: string,
    cancelButtonText: string,
    actionButtonText: string,
    onConfirm: Function
  ) {
    return (
      <ConfirmDialog
        eventScreenName="GroupEditPanel"
        glyph="caution"
        glyphColor="lava"
        title={title}
        actionButtonText={actionButtonText}
        cancelButtonText={cancelButtonText}
        onConfirm={onConfirm}
        subtext={note}
      />
    );
  }
  handleRemoveCompletion = () => {
    this.props.onRequestHide();
    this.props.router.push('/classes');
  };
  renderRemoveDialog() {
    return (
      <RemoveClassConfirmationDialog
        group={this.props.group}
        onActionCompletion={() => this.handleRemoveCompletion()}
      />
    );
  }
  renderRemoveClassButton() {
    if (this.props.group.canEdit()) {
      return (
        <ModalTrigger modal={this.renderRemoveDialog()}>
          <div>
            <Link color="lava" block data-test-selector="group-remove-class-button">
              Remove from class list
            </Link>
            <div className="GroupEditPanel-subtext">
              This class will be archived and will no longer appear in your class list.
            </div>
          </div>
        </ModalTrigger>
      );
    }
    return null;
  }
  renderReuseDialog() {
    const subText = `A new class will be created with the code @${this.props.group.getCodeOrError()}. You can always view and restore archived classes from your settings.`;
    return (
      <ReuseClassConfirmDialog
        group={this.props.group}
        onSubmit={() => this.props.onRequestHide()}
        onActionCompletion={newGroup =>
          this.props.router.push(`/classes/${newGroup.getCodeOrError()}`)
        }
        subText={subText}
        archiveFirst
      />
    );
  }
  renderReuseClassButton() {
    if (this.props.group.canEdit()) {
      return (
        <ModalTrigger modal={this.renderReuseDialog()}>
          <div>
            <Link color="lava" block>
              Reuse class code
            </Link>
            <div className="GroupEditPanel-subtext">
              This class will be archived, and a new class will be created with this code.
            </div>
          </div>
        </ModalTrigger>
      );
    }
    return null;
  }
  render() {
    const reuseClassButton = this.renderReuseClassButton();
    const removeClassButton = this.renderRemoveClassButton();
    const archiveSection = (removeClassButton || reuseClassButton) && (
      <div>
        <Divider size="medium" />
        <List size="small" className="archiveSection">
          {reuseClassButton}
          {removeClassButton}
        </List>
      </div>
    );
    return (
      <BoxBody className="GroupEditPanel">
        <MediaObject>
          <div className="GroupEditPanel-avatar">
            <GroupAvatarSelector
              ref="avatarSelector"
              onSelect={this.handleAvatarSelected}
              initialAvatar={this.props.group.getAvatar()}
            />
          </div>
          <div>
            <GroupForm
              group={this.state.group}
              name={this.props.group.getName()}
              code={this.props.group.getCodeOrError()}
              currentOrganization={this.props.group.getOrganization()}
              onFormValid={this.handleValidChange}
              onFormInvalid={this.handleInvalidChange}
            />
            <Button
              className="GroupEditPanel-submit"
              disabled={!this.state.canSubmit}
              onClick={this.handleSubmit}
              eventElementName="GroupEditPanel-submit"
              data-test-selector="group-edit-submit">
              Save
            </Button>
          </div>
        </MediaObject>

        <div>
          <Divider size="medium" />
          <GroupOptionsForm
            hasChildren={this.props.group.hasChildren()}
            hideUnderageNote={!this.props.group.getOrganization()}
            searchable={this.props.group.isSearchable()}
            showSearchable={!this.props.group.hasStudentOrParentOwner()}
            subscriberInitiatedChats={this.props.group.canSubscribersInitiateChats()}
            userIsTeacher={this.props.userIsTeacher}
            onChange={this.handleOptionsChange}
          />
        </div>

        {archiveSection}
      </BoxBody>
    );
  }
}

export { GroupEditPanel };

export default withRouter(GroupEditPanel);
